'use client';

import { useId, useState } from 'react';

import { Heading } from '@/hen-ui/atomics/Heading';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { CareerExplorerShell } from '@/hen-ui/composites/CareerExplorer/CareerExplorerShell';
import { Flex } from '@/hen-ui/composites/Flex';
import { useIsDesktop } from '@/hen-ui/hooks';
import { cn } from '@/hen-ui/utils';

import { CareerExplorerPeekExpandToggle } from './CareerExplorerPeekExpandToggle';
import { CareerExplorerSkillPills } from './CareerExplorerSkillPills';
import { CareerExplorerStatisticList } from './CareerExplorerStatisticList';
import { CareerExplorerTrajectory } from './CareerExplorerTrajectory';
import type {
  CareerExplorerPeekExpandToggle as CareerExplorerPeekExpandToggleData,
  CareerExplorerSkill,
  CareerExplorerStatistic,
  CareerExplorerTrajectory as CareerExplorerTrajectoryData,
} from './types';

const MOBILE_PREVIEW_STATISTIC_COUNT = 2;

interface CareerExplorerPeekBodyProps {
  expandToggle: CareerExplorerPeekExpandToggleData;
  skills: CareerExplorerSkill[];
  skillsHeading?: string;
  statistics: CareerExplorerStatistic[];
  title?: string;
  trajectory?: CareerExplorerTrajectoryData;
}

export const CareerExplorerPeekBody = ({
  expandToggle,
  skills,
  skillsHeading,
  statistics,
  title,
  trajectory,
}: CareerExplorerPeekBodyProps) => {
  const [expanded, setExpanded] = useState(false);

  const detailsId = useId();
  const isDesktop = useIsDesktop();

  const showDetails = isDesktop || expanded;

  const previewStatistics = isDesktop
    ? statistics
    : statistics.slice(0, MOBILE_PREVIEW_STATISTIC_COUNT);

  const overflowStatistics = isDesktop
    ? []
    : statistics.slice(MOBILE_PREVIEW_STATISTIC_COUNT);

  const skillsSection =
    skillsHeading && skills.length > 0 ? (
      <CareerExplorerSkillPills heading={skillsHeading} skills={skills} />
    ) : null;

  const trajectorySection = trajectory?.steps?.length ? (
    <CareerExplorerTrajectory {...trajectory} />
  ) : null;

  const hasHiddenDetails =
    overflowStatistics.length > 0 ||
    Boolean(skillsSection) ||
    Boolean(trajectorySection);

  return (
    <CareerExplorerShell
      data-hen-id={COMPONENT_IDS.CareerExplorerPeek}
      gap='none'
    >
      <Flex gap='lg'>
        {title && (
          <Heading
            variant='h2'
            tag='h3'
            className='font-normal text-neutral-900 md:text-h3 md:leading-h3 lg:text-h3 lg:leading-h3'
          >
            {title}
          </Heading>
        )}

        <CareerExplorerStatisticList statistics={previewStatistics} />

        {hasHiddenDetails && (
          <Flex
            gap='lg'
            hidden={!showDetails}
            id={detailsId}
            width='full'
            className={cn(!showDetails && 'hidden')}
          >
            <CareerExplorerStatisticList statistics={overflowStatistics} />
            {skillsSection}
            {trajectorySection}
          </Flex>
        )}
      </Flex>

      {!isDesktop && hasHiddenDetails && (
        <CareerExplorerPeekExpandToggle
          controlsId={detailsId}
          expanded={expanded}
          labels={expandToggle}
          onToggle={() => setExpanded((isExpanded) => !isExpanded)}
        />
      )}
    </CareerExplorerShell>
  );
};
